import { ReactNode } from "react";
import { MapPin, FileText, CheckCircle2, AlertTriangle, Loader2, Copy } from "lucide-react";

import SubmissionTypeBadge, { SubmissionType } from "components/submission-type-badge/submission-type-badge";
import { copyOllieIdToClipboard } from "utils/ollie-id";

/** The verdict of the submission being looked at, or the state of the run producing it. */
export type PlanReviewHeaderTone = "approved" | "revisions" | "running" | "failed";

const TONE_META: Record<PlanReviewHeaderTone, { className: string; icon: typeof CheckCircle2 }> = {
	approved: { className: "prph-status-approved", icon: CheckCircle2 },
	revisions: { className: "prph-status-revisions", icon: AlertTriangle },
	running: { className: "prph-status-running", icon: Loader2 },
	failed: { className: "prph-status-failed", icon: AlertTriangle }
};

interface PlanReviewPageHeaderProps {
	propertyName?: string | null;
	address?: string | null;
	/** Shown after the address as "Submitted <date>". The project screen has no single date. */
	submittedLabel?: string | null;
	status?: { label: string; tone: PlanReviewHeaderTone } | null;
	/** Small note next to the status, for example "Includes reviewer edits (2)". */
	statusNote?: string | null;
	submissionType?: SubmissionType | null;
	ahjName?: string | null;
	/** Public ID of the project (OLLIE-XXXX-XXXX). Shown next to the title with a copy action. */
	ollieId?: string | null;
	/** The buttons on the right: they differ per screen, the rest of the bar does not. */
	children?: ReactNode;
}

/**
 * The property band every plan review screen opens with: the project it belongs to,
 * where it is, what was submitted and to whom.
 *
 * Kept in one component because the three screens are read one after the other — the
 * project, one submission's report, and that submission's comments — and the band has
 * to stay put as the reader moves between them.
 */
const PlanReviewPageHeader = ({
	propertyName,
	address,
	submittedLabel,
	status,
	statusNote,
	submissionType,
	ahjName,
	ollieId,
	children
}: PlanReviewPageHeaderProps) => {
	const toneMeta = status ? TONE_META[status.tone] : null;
	const StatusIcon = toneMeta?.icon;

	const handleCopyOllieId = () => {
		if (ollieId) copyOllieIdToClipboard(ollieId);
	};

	return (
		<header className="prph">
			<div className="prph-inner">
				<div className="prph-main">
					<div className="prph-info">
						<div className="prph-titlerow">
							<h1 className="prph-title">{propertyName || "Plan Review"}</h1>
							{ollieId && (
								<button type="button" className="prph-ollie-id" onClick={handleCopyOllieId} title="Copy Ollie ID">
									<span className="prph-ollie-id-text">{ollieId}</span>
									<Copy size={14} className="prph-ollie-id-icon" />
								</button>
							)}
							{status && StatusIcon && (
								<span className={`prph-status ${toneMeta.className}`}>
									<StatusIcon size={16} />
									{status.label}
								</span>
							)}
							{statusNote && (
								<span className="prph-status-note">
									<span className="prph-status-note-dot" />
									{statusNote}
								</span>
							)}
						</div>

						<p className="prph-meta">
							{address ? (
								<span className="prph-meta-item">
									<MapPin size={16} />
									{address}
								</span>
							) : null}
							{address && submittedLabel ? <span className="prph-dot">•</span> : null}
							{submittedLabel ? (
								<span className="prph-meta-item">
									<FileText size={16} />
									{submittedLabel}
								</span>
							) : null}
						</p>

						{(submissionType || ahjName) && (
							<div className="prph-pills">
								<SubmissionTypeBadge submissionType={submissionType} full className="prph-pill-type" />
								{ahjName ? <span className="prph-pill">{ahjName}</span> : null}
							</div>
						)}
					</div>
				</div>

				{children ? (
					<div className="prph-actions" data-pdf-hide>
						{children}
					</div>
				) : null}
			</div>
		</header>
	);
};

export default PlanReviewPageHeader;
